import React, { useEffect, useState, useRef } from 'react';
import { AnalyticsSection } from './AnalyticsSection';
import { MonetizationSection } from './MonetizationSection';
import { ViewerSimulationSection } from './ViewerSimulationSection';
import { NSFWControlsSection } from './NSFWControlsSection';
import { ContentPlanningSection } from './ContentPlanningSection';
import { AudienceInsightsSection } from './AudienceInsightsSection';
import { AutoReplySection } from './AutoReplySection';
import { PredictiveCoachingSection } from './PredictiveCoachingSection';
import { ContentStoreSection } from './ContentStoreSection';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../ui/tabs';
import { BarChart3, DollarSign, Eye, ShieldAlert, Calendar, LayoutDashboard, Home, Settings, Users, Bell, MessageCircle, BrainCircuit, ShoppingBag, Sparkles, Zap, X, Rocket, Bolt, PartyPopper, Crown, Upload, Check, Info, Camera, Image as ImageIcon, Download } from 'lucide-react';
export function CreatorStudioDashboard() {
  const [activeTab, setActiveTab] = useState('analytics');
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showNsfwUpsellModal, setShowNsfwUpsellModal] = useState(false);
  const [showAIImageStudioModal, setShowAIImageStudioModal] = useState(false);
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [uploadStep, setUploadStep] = useState(1);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Handle navigation from other components
  useEffect(() => {
    const handleTabChange = (event: any) => {
      if (event.detail && event.detail.tab) {
        setActiveTab(event.detail.tab);
      }
    };
    document.addEventListener('changeCreatorTab', handleTabChange);
    return () => {
      document.removeEventListener('changeCreatorTab', handleTabChange);
    };
  }, []);
  // Handle file upload
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Simulate upload process
      setIsUploading(true);
      setUploadProgress(0);
      // Create a URL for the image preview
      const imageUrl = URL.createObjectURL(file);
      // Simulate upload progress
      const interval = setInterval(() => {
        setUploadProgress(prev => {
          if (prev >= 100) {
            clearInterval(interval);
            setIsUploading(false);
            setUploadedImage(imageUrl);
            setUploadStep(2);
            return 100;
          }
          return prev + 10;
        });
      }, 200);
    }
  };
  // Reset upload flow
  const resetUpload = () => {
    setUploadedImage(null);
    setUploadStep(1);
    setUploadProgress(0);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };
  // Trigger file input click
  const triggerFileUpload = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };
  // Handle generation process
  const handleGenerate = () => {
    // Simulate generation process
    setUploadStep(3);
    setTimeout(() => {
      // After "generation" is complete, move to results
      setUploadStep(4);
    }, 3000);
  };
  // Render the upload step content
  const renderUploadStepContent = () => {
    switch (uploadStep) {
      case 1:
        // Upload photo
        return <div className="animate-in fade-in duration-300" data-id="element-4374">
            <div className="text-center mb-6" data-id="element-4375">
              <div className="w-20 h-20 bg-pink-100 dark:bg-pink-900/30 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4376">
                <Camera size={32} className="text-pink-500" data-id="element-4377" />
              </div>
              <h2 className="text-xl font-bold mb-2" data-id="element-4378">Upload Reference Photo</h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto" data-id="element-4379">
                Upload a high-quality photo of yourself to create AI-generated
                images that look like you.
              </p>
            </div>
            {/* Photo Guidelines */}
            <div className="mb-6 border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden" data-id="element-4380">
              <div className="bg-gray-50 dark:bg-gray-800 px-4 py-3 border-b border-gray-200 dark:border-gray-700" data-id="element-4381">
                <div className="flex items-center gap-2" data-id="element-4382">
                  <Info size={18} className="text-blue-500" data-id="element-4383" />
                  <h3 className="font-medium" data-id="element-4384">Photo Guidelines</h3>
                </div>
              </div>
              <div className="p-4" data-id="element-4385">
                <div className="mb-4" data-id="element-4386">
                  <h4 className="text-green-600 dark:text-green-400 font-medium mb-2" data-id="element-4387">
                    Do's
                  </h4>
                  <ul className="space-y-2" data-id="element-4388">
                    <li className="flex items-start gap-2" data-id="element-4389">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4390">
                        <Check size={14} className="text-green-600 dark:text-green-400" data-id="element-4391" />
                      </div>
                      <span data-id="element-4392">Photo where your face is clearly visible</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-4393">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4394">
                        <Check size={14} className="text-green-600 dark:text-green-400" data-id="element-4395" />
                      </div>
                      <span data-id="element-4396">Photo with good lighting</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-4397">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4398">
                        <Check size={14} className="text-green-600 dark:text-green-400" data-id="element-4399" />
                      </div>
                      <span data-id="element-4400">Photo with a neutral expression</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-4401">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4402">
                        <Check size={14} className="text-green-600 dark:text-green-400" data-id="element-4403" />
                      </div>
                      <span data-id="element-4404">Photo of head and shoulders</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-4405">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4406">
                        <Check size={14} className="text-green-600 dark:text-green-400" data-id="element-4407" />
                      </div>
                      <span data-id="element-4408">Photo with no filters</span>
                    </li>
                  </ul>
                </div>
                <div data-id="element-4409">
                  <h4 className="text-red-600 dark:text-red-400 font-medium mb-2" data-id="element-4410">
                    Don'ts
                  </h4>
                  <ul className="space-y-2" data-id="element-4411">
                    <li className="flex items-start gap-2" data-id="element-4412">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4413">
                        <X size={14} className="text-red-600 dark:text-red-400" data-id="element-4414" />
                      </div>
                      <span data-id="element-4415">Group photo</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-4416">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4417">
                        <X size={14} className="text-red-600 dark:text-red-400" data-id="element-4418" />
                      </div>
                      <span data-id="element-4419">AI photo</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-4420">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4421">
                        <X size={14} className="text-red-600 dark:text-red-400" data-id="element-4422" />
                      </div>
                      <span data-id="element-4423">Low quality photo</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-4424">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4425">
                        <X size={14} className="text-red-600 dark:text-red-400" data-id="element-4426" />
                      </div>
                      <span data-id="element-4427">Photo with text on it</span>
                    </li>
                    <li className="flex items-start gap-2" data-id="element-4428">
                      <div className="mt-0.5 w-5 h-5 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center flex-shrink-0" data-id="element-4429">
                        <X size={14} className="text-red-600 dark:text-red-400" data-id="element-4430" />
                      </div>
                      <span data-id="element-4431">NSFW photo</span>
                    </li>
                  </ul>
                </div>
                <div className="mt-4 p-3 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800/30 rounded-lg flex items-start gap-2" data-id="element-4432">
                  <div className="mt-0.5 flex-shrink-0" data-id="element-4433">
                    <Info size={16} className="text-amber-600" data-id="element-4434" />
                  </div>
                  <p className="text-sm text-amber-800 dark:text-amber-200" data-id="element-4435">
                    Ignoring these guidelines will negatively impact your AI's
                    quality
                  </p>
                </div>
              </div>
            </div>
            {/* Upload Area */}
            <div onClick={triggerFileUpload} className="bg-gray-50 dark:bg-gray-800/50 border-2 border-dashed border-gray-300 dark:border-gray-700 rounded-lg p-8 mb-6 cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors" data-id="element-4436">
              <div className="text-center" data-id="element-4437">
                <Upload className="w-10 h-10 text-gray-400 mx-auto mb-3" data-id="element-4438" />
                <p className="mb-2 text-sm text-gray-500 dark:text-gray-400" data-id="element-4439">
                  <span className="font-semibold" data-id="element-4440">Click to upload</span> or drag
                  and drop
                </p>
                <p className="text-xs text-gray-500 dark:text-gray-400" data-id="element-4441">
                  PNG, JPG or WEBP (MAX. 5MB)
                </p>
                <input type="file" className="hidden" ref={fileInputRef} accept="image/*" onChange={handleFileChange} data-id="element-4442" />
              </div>
            </div>
            {/* Upload Progress */}
            {isUploading && <div className="mb-6" data-id="element-4443">
                <div className="flex items-center justify-between mb-1" data-id="element-4444">
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4445">
                    Uploading...
                  </span>
                  <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4446">
                    {uploadProgress}%
                  </span>
                </div>
                <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-4447">
                  <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full transition-all duration-300" style={{
                width: `${uploadProgress}%`
              }} data-id="element-4448"></div>
                </div>
              </div>}
          </div>;
      case 2:
        // Review & Generate
        return <div className="animate-in fade-in duration-300" data-id="element-4449">
            <div className="text-center mb-6" data-id="element-4450">
              <div className="w-20 h-20 bg-pink-100 dark:bg-pink-900/30 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4451">
                <ImageIcon size={32} className="text-pink-500" data-id="element-4452" />
              </div>
              <h2 className="text-xl font-bold mb-2" data-id="element-4453">Review & Generate</h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto" data-id="element-4454">
                Your photo looks good! Now let's create AI-generated images
                based on this reference.
              </p>
            </div>
            {/* Preview Image */}
            {uploadedImage && <div className="mb-6 relative" data-id="element-4455">
                <div className="aspect-square max-h-[300px] overflow-hidden rounded-lg mx-auto border border-gray-200 dark:border-gray-700" data-id="element-4456">
                  <img src={uploadedImage} alt="Your uploaded photo" className="w-full h-full object-cover" data-id="element-4457" />
                </div>
              </div>}
            {/* Generation Options */}
            <div className="mb-6 grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-4458">
              <div className="p-4 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg border border-pink-100 dark:border-pink-800/30" data-id="element-4459">
                <h3 className="font-medium mb-2" data-id="element-4460">Standard Generation</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-4461">
                  Create 5 AI images in various styles
                </p>
                <button onClick={handleGenerate} className="w-full py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm hover:shadow-lg hover:shadow-pink-500/20 transition-all" data-id="element-4462">
                  Generate (10 credits)
                </button>
              </div>
              <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30" data-id="element-4463">
                <h3 className="font-medium mb-2" data-id="element-4464">Premium Generation</h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-4465">
                  Create 15 AI images with advanced options
                </p>
                <button onClick={handleGenerate} className="w-full py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg text-sm hover:shadow-lg hover:shadow-blue-500/20 transition-all" data-id="element-4466">
                  Generate (25 credits)
                </button>
              </div>
            </div>
            {/* Back Button */}
            <div className="flex justify-between" data-id="element-4467">
              <button onClick={resetUpload} className="px-4 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-4468">
                Upload Different Photo
              </button>
              <button onClick={() => setShowAIImageStudioModal(false)} className="px-4 py-2 text-gray-500 dark:text-gray-400 text-sm hover:text-gray-700 dark:hover:text-gray-200" data-id="element-4469">
                Cancel
              </button>
            </div>
          </div>;
      case 3:
        // Generating
        return <div className="animate-in fade-in duration-300" data-id="element-4470">
            <div className="text-center mb-10" data-id="element-4471">
              <div className="w-20 h-20 bg-pink-100 dark:bg-pink-900/30 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4472">
                <Sparkles size={32} className="text-pink-500 animate-pulse" data-id="element-4473" />
              </div>
              <h2 className="text-xl font-bold mb-2" data-id="element-4474">Generating Your Images</h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto" data-id="element-4475">
                Please wait while our AI creates images based on your reference
                photo...
              </p>
            </div>
            {/* Generation Progress */}
            <div className="mb-10" data-id="element-4476">
              <div className="flex items-center justify-between mb-2" data-id="element-4477">
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4478">
                  Processing...
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-4479">
                  67%
                </span>
              </div>
              <div className="h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-4480">
                <div className="h-full bg-gradient-to-r from-pink-500 to-purple-500 rounded-full animate-pulse" style={{
                width: '67%'
              }} data-id="element-4481"></div>
              </div>
            </div>
            {/* Generation Steps */}
            <div className="space-y-3 mb-6" data-id="element-4482">
              <div className="flex items-center gap-3" data-id="element-4483">
                <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-4484">
                  <Check size={14} className="text-green-600 dark:text-green-400" data-id="element-4485" />
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4486">
                  Analyzing reference photo
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-4487">
                <div className="w-6 h-6 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-4488">
                  <Check size={14} className="text-green-600 dark:text-green-400" data-id="element-4489" />
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4490">
                  Creating facial model
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-4491">
                <div className="w-6 h-6 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center animate-pulse" data-id="element-4492">
                  <div className="w-2 h-2 bg-pink-500 rounded-full" data-id="element-4493"></div>
                </div>
                <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-4494">
                  Generating variations
                </span>
              </div>
              <div className="flex items-center gap-3" data-id="element-4495">
                <div className="w-6 h-6 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center" data-id="element-4496">
                  <div className="w-2 h-2 bg-gray-400 rounded-full" data-id="element-4497"></div>
                </div>
                <span className="text-sm text-gray-400 dark:text-gray-500" data-id="element-4498">
                  Finalizing images
                </span>
              </div>
            </div>
            <p className="text-center text-sm text-gray-500 dark:text-gray-400" data-id="element-4499">
              This typically takes 1-2 minutes. Please don't close this window.
            </p>
          </div>;
      case 4:
        // Results
        return <div className="animate-in fade-in duration-300" data-id="element-4500">
            <div className="text-center mb-6" data-id="element-4501">
              <div className="w-20 h-20 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4" data-id="element-4502">
                <Check size={32} className="text-green-500" data-id="element-4503" />
              </div>
              <h2 className="text-xl font-bold mb-2" data-id="element-4504">Generation Complete!</h2>
              <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto" data-id="element-4505">
                Your AI-generated images are ready. Here are the results based
                on your reference photo.
              </p>
            </div>
            {/* Results Grid */}
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mb-6" data-id="element-4506">
              {[...Array(6)].map((_, index) => <div key={index} className="aspect-square bg-gray-100 dark:bg-gray-800 rounded-lg overflow-hidden border border-gray-200 dark:border-gray-700" data-id="element-4507">
                  <img src={`https://uploadthingy.s3.us-west-1.amazonaws.com/uiX2i1a8UozjtjXhpnJ19F/image.png`} alt={`Generated image ${index + 1}`} className="w-full h-full object-cover" data-id="element-4508" />
                </div>)}
            </div>
            {/* Action Buttons */}
            <div className="flex flex-col space-y-3 mb-6" data-id="element-4509">
              <button className="w-full py-3 bg-gradient-to-r from-pink-600 to-purple-600 text-white font-medium rounded-lg flex items-center justify-center gap-2" data-id="element-4510">
                <Sparkles size={16} data-id="element-4511" />
                <span data-id="element-4512">Generate More Variations</span>
              </button>
              <button className="w-full py-3 bg-gradient-to-r from-blue-600 to-cyan-600 text-white font-medium rounded-lg flex items-center justify-center gap-2" data-id="element-4513">
                <Download size={16} data-id="element-4514" />
                <span data-id="element-4515">Download All Images</span>
              </button>
            </div>
            {/* Close/Done Button */}
            <div className="flex justify-center" data-id="element-4516">
              <button onClick={() => {
              setShowAIImageStudioModal(false);
              resetUpload();
            }} className="px-6 py-2 bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300 rounded-lg text-sm hover:bg-gray-200 dark:hover:bg-gray-700 transition-colors" data-id="element-4517">
                Done
              </button>
            </div>
          </div>;
      default:
        return null;
    }
  };
  return <div className="w-full max-w-6xl mx-auto flex flex-col space-y-4" data-id="element-4518">
      <div className="flex items-center justify-between" data-id="element-4519">
        <h1 className="text-2xl font-bold" data-id="element-4520">Creator Studio Dashboard</h1>
        <div className="flex items-center space-x-2" data-id="element-4521">
          <select className="bg-gray-100 dark:bg-gray-800 rounded-md px-3 py-1 text-sm" data-id="element-4522">
            <option data-id="element-4523">Last 7 days</option>
            <option data-id="element-4524">Last 30 days</option>
            <option data-id="element-4525">Last 90 days</option>
            <option data-id="element-4526">Custom range</option>
          </select>
          <button onClick={() => setShowPreviewModal(true)} className="bg-gradient-to-r from-blue-500 to-cyan-500 text-white px-4 py-1 rounded-full text-sm font-medium flex items-center gap-1" data-id="element-4527">
            <Eye size={14} data-id="element-4528" />
            <span data-id="element-4529">View as Fan</span>
          </button>
          <button className="bg-gradient-to-r from-pink-500 to-purple-500 text-white px-4 py-1 rounded-full text-sm font-medium" data-id="element-4530">
            Export Data
          </button>
        </div>
      </div>
      <div className="flex items-center justify-between mb-2" data-id="element-4531">
        <div className="flex space-x-2" data-id="element-4532">
          <div className="flex items-center space-x-1 bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400 px-3 py-1 rounded-full text-sm" data-id="element-4533">
            <Users size={14} data-id="element-4534" />
            <span data-id="element-4535">2.4k Subscribers</span>
          </div>
          <div className="flex items-center space-x-1 bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400 px-3 py-1 rounded-full text-sm" data-id="element-4536">
            <DollarSign size={14} data-id="element-4537" />
            <span data-id="element-4538">$12,540 MTD</span>
          </div>
        </div>
        <div className="flex items-center space-x-2" data-id="element-4539">
          <button className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-md text-sm" data-id="element-4540">
            <Settings size={14} data-id="element-4541" />
            <span className="hidden sm:inline" data-id="element-4542">Settings</span>
          </button>
          <button className="flex items-center space-x-1 bg-gray-100 dark:bg-gray-800 px-3 py-1 rounded-md text-sm" data-id="element-4543">
            <Bell size={14} data-id="element-4544" />
            <span className="hidden sm:inline" data-id="element-4545">Notifications</span>
          </button>
        </div>
      </div>

      {/* AI Coach Tip Banner */}
      <div className="bg-gradient-to-r from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border border-blue-100 dark:border-blue-800/30 rounded-lg p-3 flex items-center justify-between" data-id="element-4546">
        <div className="flex items-center gap-2" data-id="element-4547">
          <div className="bg-blue-100 dark:bg-blue-800/30 p-2 rounded-full" data-id="element-4548">
            <Sparkles size={18} className="text-blue-600 dark:text-blue-400" data-id="element-4549" />
          </div>
          <div data-id="element-4550">
            <h3 className="text-sm font-medium text-blue-800 dark:text-blue-300" data-id="element-4551">
              AI Coach Tip
            </h3>
            <p className="text-xs text-blue-700 dark:text-blue-400" data-id="element-4552">
              Upload more video content: Your subscribers engage 40% more with
              reels
            </p>
          </div>
        </div>
        <button className="bg-blue-100 dark:bg-blue-800/30 hover:bg-blue-200 dark:hover:bg-blue-800/50 text-blue-600 dark:text-blue-400 px-3 py-1 rounded-full text-xs font-medium transition-colors" data-id="element-4553">
          Record Now
        </button>
      </div>

      <Tabs defaultValue={activeTab} value={activeTab} className="w-full" onValueChange={setActiveTab} data-id="element-4554">
        <TabsList className="grid grid-cols-10 mb-4 overflow-x-auto" data-id="element-4555">
          <TabsTrigger value="analytics" className="flex items-center gap-2" data-id="element-4556">
            <BarChart3 className="w-4 h-4" data-id="element-4557" />
            <span className="hidden sm:inline" data-id="element-4558">Analytics</span>
          </TabsTrigger>
          <TabsTrigger value="monetization" className="flex items-center gap-2" data-id="element-4559">
            <DollarSign className="w-4 h-4" data-id="element-4560" />
            <span className="hidden sm:inline" data-id="element-4561">Monetization</span>
          </TabsTrigger>
          <TabsTrigger value="viewer-simulation" className="flex items-center gap-2" data-id="element-4562">
            <Eye className="w-4 h-4" data-id="element-4563" />
            <span className="hidden sm:inline" data-id="element-4564">Viewer Simulation</span>
          </TabsTrigger>
          <TabsTrigger value="nsfw-controls" className="flex items-center gap-2" data-id="element-4565">
            <ShieldAlert className="w-4 h-4" data-id="element-4566" />
            <span className="hidden sm:inline" data-id="element-4567">NSFW Controls</span>
          </TabsTrigger>
          <TabsTrigger value="content-planning" className="flex items-center gap-2" data-id="element-4568">
            <Calendar className="w-4 h-4" data-id="element-4569" />
            <span className="hidden sm:inline" data-id="element-4570">Content Planning</span>
          </TabsTrigger>
          <TabsTrigger value="audience-insights" className="flex items-center gap-2" data-id="element-4571">
            <Users className="w-4 h-4" data-id="element-4572" />
            <span className="hidden sm:inline" data-id="element-4573">Audience Insights</span>
          </TabsTrigger>
          <TabsTrigger value="auto-reply" className="flex items-center gap-2" data-id="element-4574">
            <MessageCircle className="w-4 h-4" data-id="element-4575" />
            <span className="hidden sm:inline" data-id="element-4576">Auto-Reply</span>
          </TabsTrigger>
          <TabsTrigger value="predictive-coaching" className="flex items-center gap-2" data-id="element-4577">
            <BrainCircuit className="w-4 h-4" data-id="element-4578" />
            <span className="hidden sm:inline" data-id="element-4579">AI Coach</span>
          </TabsTrigger>
          <TabsTrigger value="content-store" className="flex items-center gap-2" data-id="element-4580">
            <ShoppingBag className="w-4 h-4" data-id="element-4581" />
            <span className="hidden sm:inline" data-id="element-4582">Content Store</span>
          </TabsTrigger>
          <TabsTrigger value="ai-image-generator" className="flex items-center gap-2" data-id="element-4583">
            <Zap className="w-4 h-4" data-id="element-4584" />
            <span className="hidden sm:inline" data-id="element-4585">AI Image Studio</span>
          </TabsTrigger>
        </TabsList>

        <TabsContent value="analytics" className="space-y-4" data-id="element-4586">
          <AnalyticsSection data-id="element-4587" />
        </TabsContent>
        <TabsContent value="monetization" className="space-y-4" data-id="element-4588">
          <MonetizationSection data-id="element-4589" />
        </TabsContent>
        <TabsContent value="viewer-simulation" className="space-y-4" data-id="element-4590">
          <ViewerSimulationSection data-id="element-4591" />
        </TabsContent>
        <TabsContent value="nsfw-controls" className="space-y-4" data-id="element-4592">
          <NSFWControlsSection data-id="element-4593" />
        </TabsContent>
        <TabsContent value="content-planning" className="space-y-4" data-id="element-4594">
          <ContentPlanningSection data-id="element-4595" />
        </TabsContent>
        <TabsContent value="audience-insights" className="space-y-4" data-id="element-4596">
          <AudienceInsightsSection data-id="element-4597" />
        </TabsContent>
        <TabsContent value="auto-reply" className="space-y-4" data-id="element-4598">
          <AutoReplySection data-id="element-4599" />
        </TabsContent>
        <TabsContent value="predictive-coaching" className="space-y-4" data-id="element-4600">
          <PredictiveCoachingSection data-id="element-4601" />
        </TabsContent>
        <TabsContent value="content-store" className="space-y-4" data-id="element-4602">
          <ContentStoreSection data-id="element-4603" />
        </TabsContent>
        <TabsContent value="ai-image-generator" className="space-y-4" data-id="element-4604">
          <div className="bg-white dark:bg-gray-800 p-6 rounded-xl shadow-sm" data-id="element-4605">
            <div className="text-center" data-id="element-4606">
              <div className="inline-flex items-center justify-center p-3 bg-pink-100 dark:bg-pink-900/30 rounded-full mb-4" data-id="element-4607">
                <Sparkles size={24} className="text-pink-600 dark:text-pink-400" data-id="element-4608" />
              </div>
              <h2 className="text-xl font-bold mb-2" data-id="element-4609">AI Image Studio</h2>
              <p className="text-gray-500 dark:text-gray-400 mb-6 max-w-md mx-auto" data-id="element-4610">
                Create stunning AI-generated images for your content. Customize
                poses, outfits, settings and more.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6" data-id="element-4611">
                <div className="p-4 bg-gradient-to-br from-pink-50 to-purple-50 dark:from-pink-900/20 dark:to-purple-900/20 rounded-lg border border-pink-100 dark:border-pink-800/30" data-id="element-4612">
                  <h3 className="font-medium mb-2" data-id="element-4613">NSFW Content</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-4614">
                    Generate provocative and intimate images
                  </p>
                  <button onClick={() => setShowNsfwUpsellModal(true)} className="w-full py-2 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-lg text-sm hover:shadow-lg hover:shadow-pink-500/20 transition-all" data-id="element-4615">
                    Create NSFW
                  </button>
                </div>
                <div className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 rounded-lg border border-blue-100 dark:border-blue-800/30" data-id="element-4616">
                  <h3 className="font-medium mb-2" data-id="element-4617">SFW Teasers</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-4618">
                    Create suggestive but safe-for-work images
                  </p>
                  <button onClick={() => setShowNsfwUpsellModal(true)} className="w-full py-2 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg text-sm hover:shadow-lg hover:shadow-blue-500/20 transition-all" data-id="element-4619">
                    Create Teaser
                  </button>
                </div>
                <div className="p-4 bg-gradient-to-br from-green-50 to-teal-50 dark:from-green-900/20 dark:to-teal-900/20 rounded-lg border border-green-100 dark:border-green-800/30" data-id="element-4620">
                  <h3 className="font-medium mb-2" data-id="element-4621">Custom Sets</h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3" data-id="element-4622">
                    Create themed image sets for campaigns
                  </p>
                  <button onClick={() => setShowNsfwUpsellModal(true)} className="w-full py-2 bg-gradient-to-r from-green-500 to-teal-500 text-white rounded-lg text-sm hover:shadow-lg hover:shadow-green-500/20 transition-all" data-id="element-4623">
                    Create Set
                  </button>
                </div>
              </div>
              <button onClick={() => setShowAIImageStudioModal(true)} className="px-6 py-3 bg-gradient-to-r from-pink-500 to-purple-500 text-white rounded-full text-sm font-medium shadow-lg shadow-pink-500/20" data-id="element-4624">
                Open Full AI Image Studio
              </button>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* View as Fan Modal */}
      {showPreviewModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-4625">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-hidden" data-id="element-4626">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between" data-id="element-4627">
              <h3 className="font-bold flex items-center gap-2" data-id="element-4628">
                <Eye size={18} className="text-blue-500" data-id="element-4629" />
                View As Fan
              </h3>
              <button onClick={() => setShowPreviewModal(false)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-4630">
                <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="w-5 h-5" data-id="element-4631">
                  <path d="M18 6 6 18" data-id="element-4632" />
                  <path d="m6 6 12 12" data-id="element-4633" />
                </svg>
              </button>
            </div>
            <div className="p-4" data-id="element-4634">
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4" data-id="element-4635">
                Select a persona to view your profile as that user would see it
              </p>
              <div className="grid grid-cols-2 gap-3 mb-4" data-id="element-4636">
                <button className="p-3 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 flex flex-col items-center" data-id="element-4637">
                  <div className="w-12 h-12 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-600 dark:text-blue-400 mb-2" data-id="element-4638">
                    <Users size={20} data-id="element-4639" />
                  </div>
                  <span className="font-medium text-sm" data-id="element-4640">
                    First-time Visitor
                  </span>
                </button>
                <button className="p-3 border border-purple-200 dark:border-purple-800 rounded-lg hover:bg-purple-50 dark:hover:bg-purple-900/20 flex flex-col items-center" data-id="element-4641">
                  <div className="w-12 h-12 rounded-full bg-purple-100 dark:bg-purple-900/30 flex items-center justify-center text-purple-600 dark:text-purple-400 mb-2" data-id="element-4642">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4643">
                      <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" data-id="element-4644" />
                    </svg>
                  </div>
                  <span className="font-medium text-sm" data-id="element-4645">Fan Club Member</span>
                </button>
                <button className="p-3 border border-pink-200 dark:border-pink-800 rounded-lg hover:bg-pink-50 dark:hover:bg-pink-900/20 flex flex-col items-center" data-id="element-4646">
                  <div className="w-12 h-12 rounded-full bg-pink-100 dark:bg-pink-900/30 flex items-center justify-center text-pink-600 dark:text-pink-400 mb-2" data-id="element-4647">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4648">
                      <path d="M2 12C2 6.5 6.5 2 12 2a10 10 0 0 1 8 4" data-id="element-4649" />
                      <path d="M5 19.5C5.5 18 6 15 6 12c0-.7.12-1.37.34-2" data-id="element-4650" />
                      <path d="M17.29 21.02c.12-.6.43-2.3.5-3.02" data-id="element-4651" />
                      <path d="M12 10a2 2 0 0 1 2 2c0 1.02-.1 2.51-.26 4" data-id="element-4652" />
                      <path d="M8.65 22c.21-.66.45-1.32.57-2" data-id="element-4653" />
                      <path d="M14 13.12c0 2.38 0 6.38-1 8.88" data-id="element-4654" />
                      <path d="M2 16h.01" data-id="element-4655" />
                      <path d="M21.8 16c.2-2 .131-5.354 0-6" data-id="element-4656" />
                      <path d="M9 6.8a6 6 0 0 1 9 5.2c0 .47 0 1.17-.02 2" data-id="element-4657" />
                    </svg>
                  </div>
                  <span className="font-medium text-sm" data-id="element-4658">DickPic Target</span>
                </button>
                <button className="p-3 border border-green-200 dark:border-green-800 rounded-lg hover:bg-green-50 dark:hover:bg-green-900/20 flex flex-col items-center" data-id="element-4659">
                  <div className="w-12 h-12 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center text-green-600 dark:text-green-400 mb-2" data-id="element-4660">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4661">
                      <path d="M3 12a9 9 0 1 0 18 0 9 9 0 0 0-18 0" data-id="element-4662" />
                      <path d="M3 12h4" data-id="element-4663" />
                      <path d="M17 12h4" data-id="element-4664" />
                      <path d="M12 3v4" data-id="element-4665" />
                      <path d="M12 17v4" data-id="element-4666" />
                      <path d="m18.364 5.636-2.828 2.828" data-id="element-4667" />
                      <path d="m8.464 15.536-2.828 2.828" data-id="element-4668" />
                      <path d="m5.636 5.636 2.828 2.828" data-id="element-4669" />
                      <path d="m15.536 15.536 2.828 2.828" data-id="element-4670" />
                    </svg>
                  </div>
                  <span className="font-medium text-sm" data-id="element-4671">AR/VR Mode User</span>
                </button>
              </div>
              <div className="flex justify-end" data-id="element-4672">
                <button onClick={() => setShowPreviewModal(false)} className="px-4 py-2 bg-gray-100 dark:bg-gray-800 rounded-md text-sm font-medium" data-id="element-4673">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>}

      {/* NSFW AI Image Generator Upsell Modal */}
      {showNsfwUpsellModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-4674">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto" data-id="element-4675">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between" data-id="element-4676">
              <h3 className="font-bold flex items-center gap-2 text-lg" data-id="element-4677">
                <Sparkles size={20} className="text-pink-500" data-id="element-4678" />
                AI NSFW Image Generator Subscription
              </h3>
              <button onClick={() => setShowNsfwUpsellModal(false)} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-4679">
                <X size={20} data-id="element-4680" />
              </button>
            </div>
            <div className="p-6" data-id="element-4681">
              <div className="text-center mb-6" data-id="element-4682">
                <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-pink-500 to-purple-500 text-transparent bg-clip-text" data-id="element-4683">
                  Unlock Premium AI NSFW Generation
                </h2>
                <p className="text-gray-600 dark:text-gray-400 max-w-md mx-auto" data-id="element-4684">
                  Create unlimited high-quality AI-generated NSFW content for
                  your subscribers
                </p>
              </div>
              {/* Plan Toggle */}
              <div className="flex justify-center mb-6" data-id="element-4685">
                <div className="bg-gray-100 dark:bg-gray-800 p-1 rounded-full flex" data-id="element-4686">
                  <button className="px-6 py-2 rounded-full bg-white dark:bg-gray-700 shadow-sm font-medium text-sm" data-id="element-4687">
                    Monthly Plan
                  </button>
                  <button className="px-6 py-2 rounded-full text-gray-700 dark:text-gray-300 font-medium text-sm" data-id="element-4688">
                    Annual Plan
                    <span className="ml-1 text-xs bg-pink-100 dark:bg-pink-900/30 text-pink-600 dark:text-pink-400 px-1.5 py-0.5 rounded-full" data-id="element-4689">
                      ONLY NOW
                    </span>
                  </button>
                </div>
              </div>
              {/* Money Back Guarantee */}
              <div className="text-center mb-6" data-id="element-4690">
                <span className="text-orange-500 font-medium" data-id="element-4691">
                  7 DAY MONEY BACK GUARANTEE
                </span>
              </div>
              {/* Pricing Plans */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6" data-id="element-4692">
                {/* Starter Plan */}
                <div className="border border-purple-200 dark:border-purple-800/30 rounded-xl p-5 bg-gradient-to-b from-purple-50 to-white dark:from-purple-900/10 dark:to-gray-900" data-id="element-4693">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4694">
                    <div className="bg-purple-100 dark:bg-purple-900/30 p-2 rounded-full" data-id="element-4695">
                      <Rocket size={20} className="text-purple-600 dark:text-purple-400" data-id="element-4696" />
                    </div>
                    <h3 className="font-bold" data-id="element-4697">Starter</h3>
                  </div>
                  <div className="mb-3" data-id="element-4698">
                    <span className="text-3xl font-bold" data-id="element-4699">$27</span>
                    <span className="text-gray-500" data-id="element-4700">/mo</span>
                  </div>
                  <div className="space-y-2 mb-4" data-id="element-4701">
                    <p className="text-gray-700 dark:text-gray-300" data-id="element-4702">
                      $0.54/image
                    </p>
                    <p className="text-gray-700 dark:text-gray-300" data-id="element-4703">
                      50 images/month
                    </p>
                  </div>
                  <button className="w-full py-2 bg-gradient-to-r from-purple-500 to-purple-600 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-purple-500/20 transition-all" data-id="element-4704">
                    Get Started
                  </button>
                </div>
                {/* Creator Plan */}
                <div className="border border-pink-200 dark:border-pink-800/30 rounded-xl p-5 bg-gradient-to-b from-pink-50 to-white dark:from-pink-900/10 dark:to-gray-900" data-id="element-4705">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4706">
                    <div className="bg-pink-100 dark:bg-pink-900/30 p-2 rounded-full" data-id="element-4707">
                      <PartyPopper size={20} className="text-pink-600 dark:text-pink-400" data-id="element-4708" />
                    </div>
                    <h3 className="font-bold" data-id="element-4709">Creator</h3>
                  </div>
                  <div className="mb-3" data-id="element-4710">
                    <span className="text-3xl font-bold" data-id="element-4711">$99</span>
                    <span className="text-gray-500" data-id="element-4712">/mo</span>
                  </div>
                  <div className="space-y-2 mb-4" data-id="element-4713">
                    <p className="text-gray-700 dark:text-gray-300" data-id="element-4714">
                      $0.33/image
                    </p>
                    <p className="text-gray-700 dark:text-gray-300" data-id="element-4715">
                      300 images/month
                    </p>
                  </div>
                  <button className="w-full py-2 bg-gradient-to-r from-pink-500 to-pink-600 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-pink-500/20 transition-all" data-id="element-4716">
                    Popular Choice
                  </button>
                </div>
                {/* Pro Plan */}
                <div className="border border-amber-200 dark:border-amber-800/30 rounded-xl p-5 bg-gradient-to-b from-amber-50 to-white dark:from-amber-900/10 dark:to-gray-900" data-id="element-4717">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4718">
                    <div className="bg-amber-100 dark:bg-amber-900/30 p-2 rounded-full" data-id="element-4719">
                      <Bolt size={20} className="text-amber-600 dark:text-amber-400" data-id="element-4720" />
                    </div>
                    <h3 className="font-bold" data-id="element-4721">Pro</h3>
                  </div>
                  <div className="mb-3" data-id="element-4722">
                    <span className="text-3xl font-bold" data-id="element-4723">$149</span>
                    <span className="text-gray-500" data-id="element-4724">/mo</span>
                  </div>
                  <div className="space-y-2 mb-4" data-id="element-4725">
                    <p className="text-gray-700 dark:text-gray-300" data-id="element-4726">
                      $0.30/image
                    </p>
                    <p className="text-gray-700 dark:text-gray-300" data-id="element-4727">
                      500 images/month
                    </p>
                  </div>
                  <button className="w-full py-2 bg-gradient-to-r from-amber-50 to-amber-600 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-amber-500/20 transition-all" data-id="element-4728">
                    Best Value
                  </button>
                </div>
                {/* Ultimate Plan */}
                <div className="border border-blue-200 dark:border-blue-800/30 rounded-xl p-5 bg-gradient-to-b from-blue-50 to-white dark:from-blue-900/10 dark:to-gray-900" data-id="element-4729">
                  <div className="flex items-center gap-2 mb-3" data-id="element-4730">
                    <div className="bg-blue-100 dark:bg-blue-900/30 p-2 rounded-full" data-id="element-4731">
                      <Crown size={20} className="text-blue-600 dark:text-blue-400" data-id="element-4732" />
                    </div>
                    <h3 className="font-bold" data-id="element-4733">Ultimate</h3>
                  </div>
                  <div className="mb-3" data-id="element-4734">
                    <span className="text-3xl font-bold" data-id="element-4735">$249</span>
                    <span className="text-gray-500" data-id="element-4736">/mo</span>
                  </div>
                  <div className="space-y-2 mb-4" data-id="element-4737">
                    <p className="text-gray-700 dark:text-gray-300" data-id="element-4738">
                      $0.21/image
                    </p>
                    <p className="text-gray-700 dark:text-gray-300" data-id="element-4739">
                      1,200 images/month
                    </p>
                  </div>
                  <button className="w-full py-2 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-lg text-sm font-medium hover:shadow-lg hover:shadow-blue-500/20 transition-all" data-id="element-4740">
                    Go Ultimate
                  </button>
                </div>
              </div>
              {/* Features Section */}
              <div className="border border-gray-200 dark:border-gray-800 rounded-xl p-5 bg-gray-50 dark:bg-gray-800/50" data-id="element-4741">
                <h3 className="font-bold mb-4 text-center" data-id="element-4742">
                  All Plans Include:
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-id="element-4743">
                  <div className="flex items-center gap-2" data-id="element-4744">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-4745">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4746">
                        <path d="M10 3L4.5 8.5L2 6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4747" />
                      </svg>
                    </div>
                    <span className="text-sm" data-id="element-4748">
                      High-quality NSFW generations
                    </span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4749">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-4750">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4751">
                        <path d="M10 3L4.5 8.5L2 6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4752" />
                      </svg>
                    </div>
                    <span className="text-sm" data-id="element-4753">Custom pose control</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4754">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-4755">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4756">
                        <path d="M10 3L4.5 8.5L2 6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4757" />
                      </svg>
                    </div>
                    <span className="text-sm" data-id="element-4758">Advanced editing tools</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4759">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-4760">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4761">
                        <path d="M10 3L4.5 8.5L2 6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4762" />
                      </svg>
                    </div>
                    <span className="text-sm" data-id="element-4763">Body type customization</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4764">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-4765">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4766">
                        <path d="M10 3L4.5 8.5L2 6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4767" />
                      </svg>
                    </div>
                    <span className="text-sm" data-id="element-4768">Commercial usage rights</span>
                  </div>
                  <div className="flex items-center gap-2" data-id="element-4769">
                    <div className="w-5 h-5 rounded-full bg-green-100 dark:bg-green-900/30 flex items-center justify-center" data-id="element-4770">
                      <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" data-id="element-4771">
                        <path d="M10 3L4.5 8.5L2 6" stroke="#10B981" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-4772" />
                      </svg>
                    </div>
                    <span className="text-sm" data-id="element-4773">Priority support</span>
                  </div>
                </div>
              </div>
              <div className="mt-6 text-center" data-id="element-4774">
                <button onClick={() => setShowNsfwUpsellModal(false)} className="px-6 py-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 text-sm" data-id="element-4775">
                  Maybe later
                </button>
              </div>
            </div>
          </div>
        </div>}
      {/* AI Image Studio Upload Modal */}
      {showAIImageStudioModal && <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm" data-id="element-4776">
          <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-auto" data-id="element-4777">
            <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between" data-id="element-4778">
              <h3 className="font-bold flex items-center gap-2 text-lg" data-id="element-4779">
                <Sparkles size={20} className="text-pink-500" data-id="element-4780" />
                AI Image Studio
              </h3>
              <button onClick={() => {
            setShowAIImageStudioModal(false);
            resetUpload();
          }} className="p-1 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800" data-id="element-4781">
                <X size={20} data-id="element-4782" />
              </button>
            </div>
            <div className="p-6" data-id="element-4783">{renderUploadStepContent()}</div>
          </div>
        </div>}
    </div>;
}